/**
 * Heuristic parameter advisor (client-side, deterministic).
 *
 * Reads the parsed columns and proposes input_schema parameters using the same
 * column-classification rules as auto-inference. No network, no AWS, no LLM —
 * the fallback when no backend endpoint is configured (local/dev), mirroring
 * `heuristicAdvisor` for metrics.
 *
 * Rules:
 *   - low-cardinality varchar column -> array param with enum (from samples)
 *   - date column                    -> array param (date range), required
 *   - numeric column                 -> excluded, with a warning explaining why
 *   - other text column              -> string param
 */

import {
  ColumnInfo,
  ParameterAdvisor,
  ParameterDraft,
  ParameterSuggestion,
  ParameterSuggestionInput,
} from './types';
import {
  isDateColumn,
  isIdentifierColumn,
  isLowCardinalityText,
  isNumericColumn,
  isTextColumn,
  mapParamType,
} from './autoInference';

export const heuristicParameterAdvisor: ParameterAdvisor = {
  async suggest(input: ParameterSuggestionInput): Promise<ParameterSuggestion> {
    const { columns } = input;
    const parameters: ParameterDraft[] = [];
    const warnings: string[] = [];

    if (!columns || columns.length === 0) {
      warnings.push('No columns available to propose parameters from.');
      return { parameters, warnings };
    }

    for (const col of columns) {
      // Numeric (non-identifier) columns are measures, not user-facing filters.
      if (isNumericColumn(col) && !isIdentifierColumn(col)) {
        warnings.push(
          `Column '${col.columnName}' was not exposed as a parameter — numeric measure columns back metrics, not filters.`,
        );
        continue;
      }

      // Identifier columns are keys, not filters.
      if (isIdentifierColumn(col)) {
        continue;
      }

      if (isDateColumn(col)) {
        // Date columns become array (date-range) parameters; required by convention
        // (the per-row `required` flag is the single source of truth).
        parameters.push(buildParam(col, 'array', null, true));
        continue;
      }

      if (isLowCardinalityText(col)) {
        const enumValues =
          col.sampleValues !== null && col.sampleValues.length > 0 ? col.sampleValues : null;
        parameters.push(buildParam(col, 'array', enumValues, false));
        continue;
      }

      if (isTextColumn(col)) {
        parameters.push(buildParam(col, 'string', null, false));
        continue;
      }

      // Unknown type — fall back to the auto-inference mapping as a string param.
      parameters.push({
        parameterName: col.columnName,
        type: mapParamType(col, false),
        description: '',
        required: false,
        enumValues: null,
        defaultValue: null,
      });
    }

    if (parameters.length === 0 && warnings.length === 0) {
      warnings.push('No filter or dimension columns detected.');
    }

    return { parameters, warnings };
  },
};

/** Builds a ParameterDraft for a column with the given type/enum/required. */
function buildParam(
  col: ColumnInfo,
  type: ParameterDraft['type'],
  enumValues: string[] | null,
  isRequired: boolean,
): ParameterDraft {
  return {
    parameterName: col.columnName,
    type,
    description: '',
    required: isRequired,
    enumValues,
    defaultValue: null,
  };
}
